import {
  CultureVitalSign,
  CultureVitalSignRaw,
  FocusedBehaviorInMeeting,
  FocusedBehaviorInMeetingRaw,
  KeyMoment,
  KeyMomentRaw,
  V2Insight,
  V2InsightRaw,
  InviteSense,
  InviteSenseRaw,
  Meeting,
  MeetingFeedback,
  MeetingFeedbackRaw,
  MeetingListItem,
  MeetingListItemRaw,
  MeetingRaw,
  MeetingReportFeedback,
  MeetingReportFeedbackRaw,
  MeetingSummary,
  MeetingSummaryRaw,
  MeetingTopics,
  MeetingTopicsRaw,
  MeetingsAnalysis,
  MeetingsAnalysisDetail,
  MeetingsAnalysisDetailRaw,
  MeetingsAnalysisRaw,
  MeetingsListByVitalSign,
  MeetingsListByVitalSignRaw,
  SanitizedParticipant,
  SanitizedParticipantRaw,
  SentimentalAnalysis,
  SentimentalAnalysisRaw,
  ValueAlignment,
  ValueAlignmentRaw,
  CompanyIndexRaw,
  CompanyIndex,
  ParticipantAnalysis,
  ParticipantAnalysisRaw,
} from '../../interfaces/meeting';

export const sanitizeMeetingsFromRaw = (
  data: MeetingListItemRaw[]
): MeetingListItem[] => {
  return data.map((meeting) => sanitizeMeetingListItemFromRaw(meeting));
};

export const sanitizeMeetingListItemFromRaw = (
  data: MeetingListItemRaw
): MeetingListItem => {
  return {
    duration: data.duration,
    name: data.name,
    participantsNames: data.participants_names,
    startDate: data.start_date,
    uuid: data.uuid,
    valuesAlignmentProcessed: data.values_alignment_processed,
    cultureVitalSignsProcessed: data.culture_vital_signs_processed,
    recallBotStatus: data.recall_bot_status,
    recallBotStatusComment: data.recall_bot_status_comment,
    meetingType: data.meeting_type,
    presignedThumbnailUrl: data.presigned_thumbnail_url,
    meetingCultureIndex: {
      cultureIndexScore: data.meeting_culture_index.culture_index_score,
    },
    sanitizedParticipants: data.sanitized_participants
      ? sanitizeSanitizedParticipantsFromRaw(data.sanitized_participants)
      : null,
    meetingHygiene: data.meeting_hygiene,
  };
};

const sanitizeCultureIndex = (data: CompanyIndexRaw): CompanyIndex => {
  return {
    companyValue: data.company_value,
    cultureVitalSign: data.culture_vital_sign,
    customCultureKpi: data.custom_culture_kpi,
    cultureIndexScore: data.culture_index_score,
    hptiBreakdown: data.hpti_breakdown
      ? {
          meetingVitalSigns: data.hpti_breakdown.meeting_vital_signs ?? null,
          valuesAlignment: data.hpti_breakdown.values_alignment ?? null,
          meetingEffectiveness:
            data.hpti_breakdown.meeting_effectiveness ?? null,
        }
      : null,
  };
};

export const sanitizeMeetingFromRaw = (data: MeetingRaw): Meeting => {
  return {
    presignedThumbnailUrl: data.presigned_thumbnail_url ?? null,
    cultureVitalSigns: data.culture_vital_signs
      .filter((sign) => sign.slug !== 'employee_engagement')
      .map((cultureVitalSign) =>
        sanitizeCultureVitalSignsFromRaw(cultureVitalSign)
      ),
    customCultureKpi: !!data.custom_culture_kpi?.length
      ? data.custom_culture_kpi.map(
          (customCultureKpi) =>
            sanitizeCultureVitalSignsFromRaw(customCultureKpi) || null
        )
      : null,
    sentimentalAnalysis: sanitizeSentimentalAnalysisFromRaw(
      data.sentimental_analysis
    ),
    meetingType: data.meeting_type,
    duration: data.duration,
    name: data.name,
    presignedAudioUrl: data.presigned_audio_url,
    presignedVideoUrl: data.presigned_video_url,
    participantsNames: data.participants_names,
    startDate: data.start_date,
    uuid: data.uuid,
    feedback: data.feedback
      ? sanitizeMeetingFeedbackFromRaw(data.feedback)
      : null,
    valuesAlignment: data.values_alignment.map((value) =>
      sanitizeValuesAlignmentFromRaw(value)
    ),
    summary: sanitizeMeetingSummaryFromRaw(data.summary),
    sanitizedParticipants: data.sanitized_participants
      ? sanitizeSanitizedParticipantsFromRaw(data.sanitized_participants)
      : null,
    meetingHygiene: data.meeting_hygiene,
    meetingConfidence: sanitizeMeetingConfidenceFromRaw(
      data?.meeting_confidence
    ),
    participantAnalysis: data.participant_analysis
      ? {
          confidenceScore: data.participant_analysis.confidence_score,
          participants: data.participant_analysis.participants.map(
            (participant) => sanitizeParticipantAnalysisFromRaw(participant)
          ),
        }
      : null,
    cultureIndexSummary: data.culture_index_summary
      ? {
          cultureOverview: data.culture_index_summary.culture_overview || '',
          message: data.culture_index_summary.message || '',
          meetingCultureIndex: sanitizeCultureIndex(
            data.culture_index_summary.meeting_culture_index
          ),
          difference: data.culture_index_summary.difference ?? null,
        }
      : null,
    videoAvailable:
      data.video_available ??
      (data.presigned_video_url != null ? !!data.presigned_video_url : true),
    // Default false: only claim auto-deletion when the API says so.
    privacyEnabled: data.privacy_enabled ?? false,
    version: data.version ?? 1,
    keyMoments: data.key_moments
      ? data.key_moments.map((moment: any) => sanitizeKeyMomentFromRaw(moment))
      : undefined,
    youInTheMeeting: data.you_in_the_meeting
      ? sanitizeYouInTheMeetingFromRaw(data.you_in_the_meeting)
      : null,
    restrictedView: data.restricted_view ?? false,
    reportFeedback: data.report_feedback
      ? sanitizeMeetingReportFeedbackFromRaw(data.report_feedback)
      : null,
  };
};

export const sanitizeMeetingReportFeedbackFromRaw = (
  data: MeetingReportFeedbackRaw
): MeetingReportFeedback => ({
  status: data.status,
  reason: data.reason ?? null,
  context: data.context ?? null,
  submittedAt: data.submitted_at ?? null,
});

const sanitizeYouInTheMeetingFromRaw = (
  data: NonNullable<MeetingRaw['you_in_the_meeting']>
): NonNullable<Meeting['youInTheMeeting']> => {
  return {
    trendCards: (data.trend_cards || []).map((card) => ({
      traitName: card.trait_name,
      direction: card.direction,
      observationText: card.observation_text,
      frequencyStat: card.frequency_stat,
    })),
    tip: data.tip
      ? {
          tipTitle: data.tip.tip_title,
          tipBody: data.tip.tip_body,
        }
      : null,
    tips: (data.tips || []).map((tip) => ({
      title: tip.title,
      recommendation: tip.recommendation,
      whyItMatters: tip.why_it_matters ?? null,
    })),
    stats: data.stats
      ? {
          talkTimePercentage: data.stats.talk_time_percentage,
          questions: data.stats.questions,
          interruptions: data.stats.interruptions,
        }
      : null,
    impact: data.impact
      ? {
          score: data.impact.score,
          label: data.impact.label ?? null,
          summary: data.impact.summary ?? null,
        }
      : null,
    focusedBehavior: data.focused_behavior
      ? sanitizeFocusedBehaviorFromRaw(data.focused_behavior)
      : null,
  };
};

export const sanitizeFocusedBehaviorFromRaw = (
  data: FocusedBehaviorInMeetingRaw
): FocusedBehaviorInMeeting => {
  return {
    name: data.name,
    weekNumber: data.week_number,
    totalWeeks: data.total_weeks,
    practiced: data.practiced,
    observed: data.observed ?? true,
    summary: data.summary ?? null,
    keyMoments: data.key_moments ?? [],
    recentMeetings: (data.recent_meetings ?? []).map((meeting) => ({
      meetingUuid: meeting.meeting_uuid,
      practiced: meeting.practiced,
      observed: meeting.observed ?? true,
    })),
  };
};

const sanitizeParticipantAnalysisFromRaw = (
  data: ParticipantAnalysisRaw
): ParticipantAnalysis => {
  return {
    participantName: data.participant_name,
    associatedProfile: data.associated_profile,
    insufficientData: data.insufficient_data,
    vitalSigns: data.vital_signs.map((vitalSign) =>
      sanitizeCultureVitalSignsFromRaw(vitalSign)
    ),
    cultureKpis: data.culture_kpis.map((cultureKpi) =>
      sanitizeCultureVitalSignsFromRaw(cultureKpi)
    ),
    coreValues: data.core_values.map((coreValue) =>
      sanitizeCultureVitalSignsFromRaw(coreValue)
    ),
  };
};

const sanitizeMeetingConfidenceFromRaw = (
  data?: MeetingRaw['meeting_confidence']
): Meeting['meetingConfidence'] => {
  return {
    cultureArchiveConfidence: {
      confidenceScore: data?.culture_archive_confidence?.confidence_score || 0,
      message: data?.culture_archive_confidence?.message || '',
    },
    speakerDistribution: {
      confidenceScore: data?.speaker_distribution?.confidence_score || 0,
      message: data?.speaker_distribution?.message || '',
    },
    transcriptDepth: {
      confidenceScore: data?.transcript_depth?.confidence_score || 0,
      message: data?.transcript_depth?.message || '',
    },
    overallConfidence: data?.overall_confidence || 0,
  };
};

export const sanitizeCultureVitalSignsFromRaw = (
  data: CultureVitalSignRaw
): CultureVitalSign => {
  return {
    examples: data.examples,
    feedback: data.feedback,
    name: data.name,
    score: data.score,
    slug: data.slug,
    companyScore: data.company_score,
    impact: data.impact,
    recommendations: data.recommendations,
    whatsWorking: data.whats_working
      ? data.whats_working.map(sanitizeV2Insight)
      : undefined,
    impactOpportunities: data.impact_opportunities
      ? data.impact_opportunities.map(sanitizeV2Insight)
      : undefined,
  };
};

const sanitizeV2Insight = (data: V2InsightRaw): V2Insight => {
  return {
    insight: data.insight,
    quote: data.quote,
    speaker: data.speaker,
  };
};

export const sanitizeMeetingSummaryFromRaw = (
  data: MeetingSummaryRaw
): MeetingSummary => {
  return {
    summary: data.summary,
    shortSummary: data.short_summary,
    topics: data.topics
      ? data.topics.map((topic) => sanitizeTopicsFromRaw(topic))
      : [],
    participantTakeaways:
      data.participant_takeaways?.map((participant) => {
        return {
          participant: participant.participant,
          takeaways: participant.takeaways,
          associatedProfile: participant.associated_profile,
        };
      }) || null,
  };
};

export const sanitizeSentimentalAnalysisFromRaw = (
  data: SentimentalAnalysisRaw
): SentimentalAnalysis => {
  return {
    positive: data?.positive,
    neutral: data?.neutral,
    negative: data?.negative,
  };
};

export const sanitizeTopicsFromRaw = (
  data: MeetingTopicsRaw
): MeetingTopics => {
  return {
    topic: data.title || data.topic || '',
    sequence: data.sequence,
    relevance: data.relevance || 0,
    percentage: data.percentage,
    summary: data.summary,
    decisionsMade: data.decisions_made,
    timestampLabel: data.timestamp_label,
    timestampStartSeconds: data.timestamp_start_seconds,
  };
};

export const sanitizeKeyMomentFromRaw = (data: KeyMomentRaw): KeyMoment => {
  return {
    sequence: data.sequence,
    title: data.title,
    timestampStartSeconds: data.timestamp_start_seconds,
    timestampLabel: data.timestamp_label,
    summary: data.summary,
    decisionsMade: data.decisions_made,
    decisionOwner: data.decision_owner ?? null,
  };
};

export const sanitizeValuesAlignmentFromRaw = (
  data: ValueAlignmentRaw
): ValueAlignment => {
  return {
    examples: data.examples,
    feedback: data.feedback,
    name: data.name,
    score: data.score,
    slug: data.slug,
    companyScore: data.company_score,
    impact: data.impact,
    recommendations: data.recommendations,
    whatsWorking: data.whats_working
      ? data.whats_working.map(sanitizeV2Insight)
      : undefined,
    impactOpportunities: data.impact_opportunities
      ? data.impact_opportunities.map(sanitizeV2Insight)
      : undefined,
  };
};

export const sanitizeInviteSenseForRaw = (
  data: InviteSense
): InviteSenseRaw => {
  return {
    meeting_url: data.meetingUrl,
  };
};

export const sanitizeInviteSenseFromRaw = (
  data: InviteSenseRaw
): InviteSense => {
  return {
    meetingUrl: data.meeting_url,
  };
};

export const sanitizeMeetingFeedbackFromRaw = (
  data: MeetingFeedbackRaw
): MeetingFeedback => {
  return {
    isPositive: data.is_positive,
  };
};

export const sanitizeMeetingFeedbackForRaw = (
  data: MeetingFeedback
): MeetingFeedbackRaw => {
  return {
    is_positive: data.isPositive,
  };
};

export const sanitizeMeetingsAnalysisFromRaw = (
  data: MeetingsAnalysisRaw
): MeetingsAnalysis => ({
  pastDay: sanitizeMeetingsAnalysisDetail(data.past_day),
  pastMonth: sanitizeMeetingsAnalysisDetail(data.past_month),
  pastWeek: sanitizeMeetingsAnalysisDetail(data.past_week),
});

export const sanitizeMeetingsAnalysisDetail = (
  data: MeetingsAnalysisDetailRaw
): MeetingsAnalysisDetail => ({
  avgAnalysis: {
    negative: data.avg_analysis.negative,
    neutral: data.avg_analysis.neutral,
    positive: data.avg_analysis.positive,
  },
  count: data.count,
});

export const sanitizeMeetingsListByVitalSignFromRaw = (
  data: MeetingsListByVitalSignRaw
): MeetingsListByVitalSign => {
  return {
    lastFourMeetings: data.last_four_meetings.map((meeting) => {
      return {
        id: meeting.id,
        createdAt: meeting.created_at,
        updatedAt: meeting.updated_at,
        uuid: meeting.uuid,
        duration: meeting.duration,
        startDate: meeting.start_date,
        participantsCount: meeting.participants_count,
        platform: meeting.platform,
        name: meeting.name,
        participantsNames: meeting.participants_names,
      };
    }),
    meetingsAddedByUser: data.meetings_added_by_user,
  };
};

const sanitizeSanitizedParticipantsFromRaw = (
  data: SanitizedParticipantRaw[]
): SanitizedParticipant[] => {
  return data?.map((participant) => {
    return {
      name: participant.name,
      associatedProfile: participant.associated_profile
        ? {
            name: participant.associated_profile.name,
            picture: participant.associated_profile.picture,
            uuid: participant.associated_profile.uuid,
          }
        : null,
    };
  });
};
